"use client";

import { useEffect, useRef } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useSubscription } from "@/features/subscription/queries";
import {
  bookingCalendarTarget,
  bookingCalendarUrl,
  isConsultOnlySurface,
  withBookingUtms,
  type BookingCalendarTarget,
  type BookingSurface,
} from "@/lib/utils/calendly";
import { trackFunnel } from "@/lib/integrations/sentry-tracking";
import { pushDataLayer } from "@/lib/integrations/ga4";

/**
 * Booking-CTA copy for every prospect on every booking surface (the winning arm of the retired
 * `booking-cta-copy` test). The arm id still rides utm_term so demo_bookings stays segmentable.
 */
export const BOOKING_CTA_LABEL = { key: "TOPBAR.BOOK_CALL_STRATEGY", text: "Free strategy call" };
export const BOOKING_CTA_ARM = "D";

/**
 * Paying customers never see the acquisition copy: "free strategy call"
 * reads as a sales pitch to someone who already bought. They get one fixed
 * account-team label and no utm_term.
 */
export const BOOKING_CTA_CUSTOMER_LABEL = {
  key: "TOPBAR.BOOK_CALL_CUSTOMER",
  text: "Talk to your account team",
};
export const BOOKING_CTA_CUSTOMER_ARM = "customer";

/**
 * Free-mail prospects book the free-call calendar, so the label says so. It
 * replaces the strategy-call wording for them; the arm id still rides utm_term.
 */
export const BOOKING_CTA_FREE_CALL_LABEL = {
  key: "TOPBAR.BOOK_CALL_FREE",
  text: "Book a free call",
};

export interface BookingCta {
  /** Server-computed sales qualification — UI-chrome gating only. */
  qualified: boolean;
  /**
   * Active paid subscription. Interruptive acquisition surfaces (banners,
   * exit-intent) must stay silent for these accounts; only the persistent
   * topbar CTA remains, with account-team copy.
   */
  isPaidActive: boolean;
  arm: string;
  label: { key: string; text: string };
  target: BookingCalendarTarget;
  /** Fully UTM-stamped Calendly URL for this surface (arm in utm_term, userId in salesforce_uuid). */
  href: string;
  trackClick: () => void;
}

/**
 * Single source of truth for rendering a booking CTA on any surface: copy,
 * calendar routing (consult vs free-call vs customer-meeting), UTM-stamped href, and the
 * uniform `booking / cta_clicked` funnel event.
 */
export function useBookingCta(surface: BookingSurface): BookingCta {
  const { user } = useAuth();
  const { data: subscription } = useSubscription();

  const routedTarget = bookingCalendarTarget(subscription, user?.isCompanyDomain);
  // bookingCalendarTarget owns the paid-active rule; deriving from its result
  // keeps one definition of "paying customer" across routing and gating.
  const isPaidActive = routedTarget === "customer-meeting";
  // Consult-only surfaces (the locked multimodal upsell) sell a capability the
  // workspace lacks, so paying accounts book the consult calendar, not the
  // account team. Prospects keep their domain-routed calendar; copy and gating
  // still follow the account's real paid state.
  const target = isConsultOnlySurface(surface) && isPaidActive ? "consult" : routedTarget;
  const effectiveArm = isPaidActive ? BOOKING_CTA_CUSTOMER_ARM : BOOKING_CTA_ARM;
  const label = isPaidActive
    ? BOOKING_CTA_CUSTOMER_LABEL
    : target === "free-call"
      ? BOOKING_CTA_FREE_CALL_LABEL
      : BOOKING_CTA_LABEL;
  const href = withBookingUtms(bookingCalendarUrl(target), surface, {
    userId: user?._id,
    // Paying customers carry no copy-version tag in utm_term.
    arm: isPaidActive ? undefined : BOOKING_CTA_ARM,
  });

  return {
    qualified: user?.salesQualified === true,
    isPaidActive,
    arm: effectiveArm,
    label,
    target,
    href,
    trackClick: () => {
      trackFunnel("booking", "cta_clicked", { surface, target, arm: effectiveArm });
      // trackFunnel is a Sentry breadcrumb only — GA4 needs the explicit dataLayer push.
      pushDataLayer("Booking", "CTA Clicked", surface, undefined, {
        surface,
        target,
        arm: effectiveArm,
      });
    },
  };
}

/**
 * Fire a once-per-mount booking impression (`booking / cta_shown` breadcrumb +
 * GA4 `Booking / CTA Shown`) when `active` first becomes true. Pairs with
 * trackClick so every surface reports shown → clicked → demo_booked. Call BEFORE
 * any early return (Rules of Hooks); pass the exact visibility condition.
 */
export function useBookingImpression(cta: BookingCta, surface: BookingSurface, active: boolean): void {
  const fired = useRef(false);
  useEffect(() => {
    if (!active || fired.current) return;
    fired.current = true;
    trackFunnel("booking", "cta_shown", { surface, target: cta.target, arm: cta.arm });
    pushDataLayer("Booking", "CTA Shown", surface, undefined, { surface, target: cta.target, arm: cta.arm });
  }, [active, surface, cta.target, cta.arm]);
}
